import type { GameEvent } from '../core';
import { TILE_DUCK_GAIN } from './levels';

/** How far the tile chimes drop while a voice line is speaking. 6 dB. */
export const CHIME_DUCK = TILE_DUCK_GAIN;

/** Bright bonus line. The original mp3 peaks at about -0.8 dBTP and is not played. */
export const BONUS_VOICE_OGG = '/audio/vo-bonus-turn-bright.ogg';
export const BONUS_VOICE_M4A = '/audio/vo-bonus-turn-bright.m4a';

/** Ogg first. M4a is the fallback. The hot original is not in this list. */
export function bonusVoiceSources(support: { ogg: boolean; m4a: boolean }): string[] {
  if (support.ogg && support.m4a) return [BONUS_VOICE_OGG, BONUS_VOICE_M4A];
  if (support.ogg) return [BONUS_VOICE_OGG];
  if (support.m4a) return [BONUS_VOICE_M4A];
  return [BONUS_VOICE_OGG, BONUS_VOICE_M4A];
}

export function chimeGain(voicePlaying: boolean): number {
  return voicePlaying ? CHIME_DUCK : 1;
}

/**
 * Speak with the instant banner, once per grant. A later bonus in the same
 * turn speaks again. The cue sits on the match the banner rides. A banner
 * that is not beside a match speaks on the bonus-move event itself.
 */
export function bonusVoiceAt(events: readonly GameEvent[], index: number): boolean {
  const event = events[index];
  if (!event) return false;
  if (event.type === 'match') {
    const next = events[index + 1];
    return next?.type === 'bonus-move';
  }
  if (event.type === 'bonus-move') {
    return events[index - 1]?.type !== 'match';
  }
  return false;
}

/** Events that end a wave. A bonus cue before one of these shares the cascade's beat. */
const WAVE_END = new Set<GameEvent['type']>([
  'clear',
  'gravity',
  'cascade',
  'swap',
  'power',
  'powerup',
  'turn-ended',
  'reshuffle',
  'match-ended',
  'sudden-death',
]);

/**
 * The cascade and the bonus banner are one beat: playback speaks the cascade
 * and then the match before the highlight yields. If that wave grants a bonus,
 * the exclamation stays quiet and the bonus clip is the only line.
 */
export function bonusInWave(events: readonly GameEvent[], index: number): boolean {
  const event = events[index];
  if (!event || event.type !== 'cascade') return false;
  for (let cursor = index + 1; cursor < events.length; cursor++) {
    const next = events[cursor];
    if (WAVE_END.has(next.type)) break;
    if (bonusVoiceAt(events, cursor)) return true;
  }
  return false;
}
